import { useEffect, useRef, useState } from "react";

import { type FieldDef, visibleFields } from "./field-list";

const CHOOSE_DELAY_MS = 180;

function withField<T>(draft: T, key: keyof T, value?: string): T {
	return { ...draft, [key]: value } as T;
}

export function useFieldStep<
	TDraft extends object,
	TKind extends string,
	F extends FieldDef<TDraft, TKind>,
>(args: {
	active: boolean;
	fields: F[];
	draft: TDraft;
	setField: (key: keyof TDraft, value: string | undefined) => void;
	maskFor: (kind: TKind) => (value: string) => string;
	fieldError: (field: F) => string | null;
	validateField: (field: F) => boolean;
	onComplete: () => void;
	onChange?: (field: F, masked: string) => void;
	initialStep?: number;
}) {
	const [step, setStep] = useState(args.initialStep ?? 0);
	const [touched, setTouched] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const fields = visibleFields(args.fields, args.draft);
	const field = fields[Math.min(step, fields.length - 1)];
	const value = String(args.draft[field.key] ?? "");
	const error = touched ? args.fieldError(field) : null;

	useEffect(() => {
		if (!args.active) {
			return;
		}

		inputRef.current?.focus({ preventScroll: true });
		setTouched(false);
	}, [step, args.active]);

	const completeOrStep = (nextDraft: TDraft, delay: number) => {
		const upcoming = visibleFields(args.fields, nextDraft);

		const run = () => {
			if (step >= upcoming.length - 1) {
				args.onComplete();
				return;
			}

			setStep((s) => s + 1);
		};

		if (delay > 0) {
			setTimeout(run, delay);
			return;
		}

		run();
	};

	const set = (raw: string) => {
		const masked = args.maskFor(field.kind)(raw);

		args.setField(field.key, masked);
		args.onChange?.(field, masked);
	};

	const advance = () => {
		const valid = args.validateField(field);

		if (!valid) {
			setTouched(true);
			return;
		}

		completeOrStep(args.draft, 0);
	};

	const back = () => {
		setStep((s) => Math.max(0, s - 1));
	};

	const skip = () => {
		const next = withField(args.draft, field.key);

		args.setField(field.key, undefined);
		completeOrStep(next, 0);
	};

	const choose = (option: string) => {
		const next = withField(args.draft, field.key, option);

		args.setField(field.key, option);
		completeOrStep(next, CHOOSE_DELAY_MS);
	};

	return {
		state: { step, fields, field, value, error, inputRef },
		actions: { set, advance, back, skip, choose },
		setStep,
	};
}
